import { useState, useEffect } from 'react';
import type { MaterialItem } from '@/api/materialInfo';
import { formatDateOnly } from '@/utils/date';
import { displayVersionLabel } from '@/utils/materialVersion';

interface FileCardProps {
  material: MaterialItem;
  onPreview: (material: MaterialItem) => void;
  onDownload?: (material: MaterialItem) => void;
  loading?: boolean;
  /** 分类ID → 显示名称 映射（从分类树构建） */
  categoryLabelMap?: Map<string, string>;
}

function canDownload(material: MaterialItem): boolean {
  if (material.isVideo) return false;
  return material.downloadAllowed;
}

/** 格式化上传日期（本地时区 YYYY-MM-DD） */
function formatUploadDate(uploadDate: number | string): string {
  return formatDateOnly(uploadDate);
}

/** 文件类型配置 */
const typeConfigMap: Record<string, { icon: string; color: string; label: string }> = {
  PDF:   { icon: 'ri-file-pdf-line',   color: 'text-red-500',    label: 'PDF' },
  PPT:   { icon: 'ri-file-ppt-line',   color: 'text-orange-500', label: 'PPT' },
  Video: { icon: 'ri-video-line',      color: 'text-purple-600', label: 'Video' },
  Excel: { icon: 'ri-file-excel-line', color: 'text-green-600',  label: 'Excel' },
  Word:  { icon: 'ri-file-word-line',  color: 'text-blue-600',   label: 'Word' },
};

/** 缩略图占位图标（与后台资料管理页保持一致） */
const typeThumbnailConfig: Record<string, { icon: string; color: string; bg: string }> = {
  Video: { icon: 'ri-video-line',        color: '#9CA3AF', bg: '#1F2937' },
  PDF:   { icon: 'ri-file-pdf-2-line',   color: '#DC2626', bg: '#FEF2F2' },
  PPT:   { icon: 'ri-file-ppt-2-line',   color: '#D04423', bg: '#FFF0ED' },
  Excel: { icon: 'ri-file-excel-2-line', color: '#1E7E45', bg: '#EDFAF2' },
  Word:  { icon: 'ri-file-word-2-line',  color: '#1A5CA8', bg: '#EDF3FC' },
};

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-video bg-gray-200"></div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-3 bg-gray-100 rounded w-1/3"></div>
        <div className="flex gap-2 pt-1">
          <div className="h-8 bg-gray-200 rounded-lg flex-1"></div>
          <div className="h-8 bg-gray-200 rounded-lg flex-1"></div>
        </div>
      </div>
    </div>
  );
}

export { SkeletonCard };

function VideoCard({ material, onPreview, categoryLabelMap }: { material: MaterialItem; onPreview: (m: MaterialItem) => void; categoryLabelMap?: Map<string, string> }) {
  const [hovered, setHovered] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const showPlaceholder = !material.thumbnail || imgError;

  const primaryLabel = material.primaryCategory ? categoryLabelMap?.get(material.primaryCategory) : undefined;
  const categoryLabel = categoryLabelMap?.get(material.category) || material.category;

  return (
    <div
      className="bg-white rounded-xl overflow-hidden border border-gray-100 transition-all duration-300 cursor-pointer group"
      style={{
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onPreview(material)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden bg-gray-900">
        {!showPlaceholder && (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 animate-pulse"></div>
            )}
            <img
              src={material.thumbnail}
              alt={material.title}
              className={`w-full h-full object-cover object-top transition-all duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'} ${hovered ? 'scale-105' : 'scale-100'}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
            <div className="absolute inset-0 bg-black/30"></div>
            <div className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}></div>
          </>
        )}

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex items-center justify-center rounded-full transition-all duration-300"
            style={{
              width: hovered ? '52px' : '44px',
              height: hovered ? '52px' : '44px',
              background: hovered ? '#0069BD' : 'rgba(0,105,189,0.85)',
              boxShadow: hovered ? '0 4px 16px rgba(0,105,189,0.5)' : '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <i className="ri-play-fill text-white text-xl ml-0.5"></i>
          </div>
        </div>

        {/* Duration badge */}
        {material.videoDuration && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs font-mono font-medium">
            {material.videoDuration}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
          {material.title}
        </h3>
        {/* 一级分类 → 二级分类 */}
        {(primaryLabel || categoryLabel) && (
          <div className="flex items-center gap-1.5 mb-2">
            {primaryLabel && (
              <span className="text-xs text-gray-400 font-medium">{primaryLabel}</span>
            )}
            {primaryLabel && categoryLabel && (
              <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            )}
            {categoryLabel && (
              <span className="text-xs font-semibold" style={{ color: '#0069BD' }}>{categoryLabel}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {formatUploadDate(material.uploadDate)}
            <span className="text-gray-300">·</span>
            <span>{displayVersionLabel(material.currentVersion)}</span>
          </span>
          <span className="flex items-center gap-1 font-medium text-purple-600">
            <i className="ri-video-line text-xs"></i>
            Video
          </span>
        </div>
      </div>
    </div>
  );
}

export default function FileCard({ material, onPreview, onDownload, categoryLabelMap }: FileCardProps) {
  const [hovered, setHovered] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  if (material.isVideo) {
    return <VideoCard material={material} onPreview={onPreview} categoryLabelMap={categoryLabelMap} />;
  }

  const downloadAllowed = canDownload(material);
  const typeCfg = typeConfigMap[material.type] ?? { icon: 'ri-file-line', color: 'text-gray-500', label: material.type };
  const thumbCfg = typeThumbnailConfig[material.type] ?? typeThumbnailConfig.PDF;

  const showPlaceholder = !material.thumbnail || imgError;

  const primaryLabel = material.primaryCategory ? categoryLabelMap?.get(material.primaryCategory) : undefined;
  const categoryLabel = categoryLabelMap?.get(material.category) || material.category;

  return (
    <div
      className="bg-white rounded-xl overflow-hidden border border-gray-100 transition-all duration-300 cursor-pointer group"
      style={{
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden bg-gray-100">
        {showPlaceholder ? (
          <div className="w-full h-full flex items-center justify-center" style={{ background: thumbCfg.bg }}>
            <i className={thumbCfg.icon} style={{ color: thumbCfg.color, fontSize: '48px' }}></i>
          </div>
        ) : (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse"></div>
            )}
            <img
              src={material.thumbnail}
              alt={material.title}
              className={`w-full h-full object-cover object-top transition-all duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'} ${hovered ? 'scale-105' : 'scale-100'}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
          </>
        )}
        {/* Hover overlay */}
        <div className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}></div>

        {/* Hover action buttons */}
        <div className={`absolute inset-0 flex items-center justify-center gap-2 px-4 transition-all duration-300 ${hovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(material); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-white transition-all whitespace-nowrap cursor-pointer"
            style={{ background: '#0069BD' }}
          >
            <i className="ri-eye-line text-xs"></i>
            Preview
          </button>
          {downloadAllowed ? (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload?.(material); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-white border border-white/60 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line text-xs"></i>
              Download
            </button>
          ) : (
            <div className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-white/50 border border-white/20 whitespace-nowrap cursor-not-allowed">
              <i className="ri-eye-line text-xs"></i>
              View Only
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
          {material.title}
        </h3>

        {/* 一级分类 → 二级分类 */}
        {(primaryLabel || categoryLabel) && (
          <div className="flex items-center gap-1.5 mb-2.5">
            {primaryLabel && (
              <span className="text-xs text-gray-400">{primaryLabel}</span>
            )}
            {primaryLabel && categoryLabel && (
              <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            )}
            {categoryLabel && (
              <span className="text-xs font-semibold" style={{ color: '#0069BD' }}>{categoryLabel}</span>
            )}
          </div>
        )}

        {/* Meta row: 日期 + 格式文字 */}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {formatUploadDate(material.uploadDate)}
            <span className="text-gray-300">·</span>
            <span>{displayVersionLabel(material.currentVersion)}</span>
          </span>
          <span className={`flex items-center gap-1 font-medium ${typeCfg.color}`}>
            <i className={`${typeCfg.icon} text-xs`}></i>
            {typeCfg.label}
          </span>
        </div>
      </div>
    </div>
  );
}
